import React, { useCallback } from 'react';
import useTripStore from '../../store/useTripStore';
import {
  getExtrasAmountPerPax,
  getExtrasCurrency,
  getServiceSelections,
  removeServiceFromExtras,
  sumExtrasPrice,
} from '../../logic/serviceExtras';
import TripDayExperienceServiceChips from './TripDayExperienceServiceChips';
import type { Translate } from './types';

interface ServicesParams {
  dayIndex: number;
  experience: any;
  experienceExtras: any[];
  accomodationExtras: string[];
  selectedExtras: any;
  extrasPrices?: any[];
  currency: string;
  tripFilters: any;
  experienceStartIndex: number;
  readOnly: boolean;
  t: Translate;
}

// Owns the additional-services bands: their derived counts/totals, the
// service-chip render (shared between the day and global bands) and the
// chip-removal write.
export function useTripDayExperienceServices({
  dayIndex,
  experience,
  experienceExtras,
  accomodationExtras,
  selectedExtras,
  extrasPrices,
  currency,
  tripFilters,
  experienceStartIndex,
  readOnly,
  t,
}: ServicesParams) {
  const updateExtrasForDay = useCallback(
    (targetDayIndex: number, updateFn: (extras: any[]) => any[]) => {
      useTripStore.setState((state: any) => ({
        days: state.days.map((day: any, index: number) => {
          if (index === targetDayIndex) {
            const currentExtras = Array.isArray(day.selectedExtras)
              ? day.selectedExtras
              : [];
            return {
              ...day,
              selectedExtras: updateFn(currentExtras),
            };
          }
          return day;
        }),
      }));
    },
    [],
  );

  const { globalSelectedExtras, daySelectedExtras, globalAvailableCount } =
    getServiceSelections({
      experience,
      experienceExtras,
      accomodationExtras,
      selectedExtras,
      extrasPrices,
    });

  // The frame a global service spreads over. `durationDays` is the length of
  // THIS instance, so a repeated experience gets its own range (§2).
  const serviceFrame = {
    startDayIndex: experienceStartIndex,
    endDayIndex:
      experienceStartIndex + Math.max(experience?.durationDays ?? 1, 1) - 1,
  };

  // The service is removed from the day it is filed on — a global one lives
  // on any day of the instance, not necessarily the one showing the band.
  const removeSelectedService = (target: any) => {
    const targetDayIndex = Number.isInteger(target?.dayIndex)
      ? target.dayIndex
      : dayIndex;

    updateExtrasForDay(targetDayIndex, (currentExtras) =>
      removeServiceFromExtras(currentExtras, target),
    );
  };

  const renderServiceChips = (extras: any[], scope: any) => (
    <TripDayExperienceServiceChips
      extras={extras}
      scope={scope}
      departureDate={tripFilters?.departureDate}
      serviceFrame={serviceFrame}
      entries={experience?.extras}
      currency={currency}
      onRemove={readOnly ? undefined : removeSelectedService}
      t={t}
    />
  );

  return {
    updateExtrasForDay,
    globalSelectedExtras,
    globalExtrasCount: globalSelectedExtras.length,
    daySelectedExtras,
    dayExtrasCount: daySelectedExtras.length,
    globalAvailableCount,
    sumExtrasPrice,
    extrasCurrency: (extras: any[]) => getExtrasCurrency(extras, currency),
    extrasAmountPerPax: getExtrasAmountPerPax,
    renderServiceChips,
  };
}
